// starred nav destinations (FI-1869 round 22): the Favorites section that
// rides the top of the horizon sidebar, plus the star buttons on page headers
// and the report dashboard cards that fill it.
//
// a favorite is stored as an opaque canonical KEY minted from a router
// destination -- 'map_fleet', or 'help_center?tab=articles' when the
// destination pins a query. keys are only ever compared, never parsed: the
// rail renders a favorite by finding the registry entry whose own key matches,
// which means a favorite the user has since lost access to simply stops
// rendering (gates are re-applied on every resolve, so a stale star can never
// leak a destination).
//
// persistence is SERVER side, under the per-user preferences map alongside
// site_theme and dashboard_widgets. localStorage is not an option here:
// login.vue's created() hook calls localStorage.clear() on every visit to the
// login page, so a local-only star would evaporate at each sign-in -- which is
// exactly what happened to the old rds_favorites key this module replaces.
import Vue from 'vue'
import { get_nav_favorites, update_nav_favorites } from '@/networking/apis'

// legacy report-card favorites (route names only, localStorage). read once so
// a user's existing stars migrate into the synced list instead of vanishing.
const LEGACY_KEY = 'rds_favorites'

export const favState = Vue.observable({
  keys: [],
  loaded: false,
})

// canonical key for a router destination. query keys are sorted so two items
// that pin the same query in a different order still compare equal.
export function favKey(to) {
  if (!to) return null
  const name = typeof to === 'string' ? to : to.name
  if (!name) return null
  const query = (typeof to === 'object' && to.query) || null
  if (!query) return name
  const parts = Object.keys(query)
    .filter(k => query[k] != null && query[k] !== '')
    .sort()
    .map(k => k + '=' + String(query[k]))
  return parts.length ? name + '?' + parts.join('&') : name
}

export function isFavorite(to) {
  const key = favKey(to)
  return !!key && favState.keys.indexOf(key) !== -1
}

export function favoriteCount() {
  return favState.keys.length
}

// newest star goes last, so the section keeps a stable, user-built order
export function toggleFavorite(to) {
  const key = favKey(to)
  if (!key) return
  const idx = favState.keys.indexOf(key)
  // replace the array so vue 2 reactivity fires for every reader
  favState.keys = idx === -1
    ? favState.keys.concat([key])
    : favState.keys.slice(0, idx).concat(favState.keys.slice(idx + 1))
  persist()
}

// drag-and-drop reordering (fi_favorites_bar.vue). the caller hands back its
// own on-screen order as a full list of keys; anything missing from it (a
// star removed by a concurrent toggle mid-drag) is dropped, and anything the
// caller doesn't know about (a star added elsewhere while dragging) is
// appended rather than lost, so a race never silently deletes a favorite.
export function reorderFavorites(newOrder) {
  const currentSet = new Set(favState.keys)
  const seen = new Set()
  const next = []
  ;(newOrder || []).forEach(key => {
    if (currentSet.has(key) && !seen.has(key)) {
      seen.add(key)
      next.push(key)
    }
  })
  favState.keys.forEach(key => {
    if (!seen.has(key)) {
      seen.add(key)
      next.push(key)
    }
  })
  favState.keys = next
  persist()
}

// keyboard reordering (Alt+Arrow on a favorites bar chip): move one favorite
// a single slot earlier (delta -1) or later (delta +1). a no-op past either
// end, so a repeated keypress at the boundary is silent rather than throwing.
export function nudgeFavorite(key, delta) {
  const keys = favState.keys.slice()
  const from = keys.indexOf(key)
  if (from === -1) return
  const to = from + delta
  if (to < 0 || to >= keys.length) return
  keys.splice(from, 1)
  keys.splice(to, 0, key)
  favState.keys = keys
  persist()
}

function readLegacyFavorites() {
  try {
    const raw = JSON.parse(localStorage.getItem(LEGACY_KEY) || '[]')
    return Array.isArray(raw) ? raw.filter(r => typeof r === 'string' && r) : []
  } catch (_) {
    return []
  }
}

// instant paint from the login payload (user.preferences), so the rail does
// not flash an empty Favorites section while the GET is in flight
function readCachedUserFavorites() {
  try {
    const user = JSON.parse(localStorage.getItem('user') || 'null')
    const saved = user && user.preferences && user.preferences.nav_favorites
    return Array.isArray(saved) ? saved.filter(k => typeof k === 'string' && k) : null
  } catch (_) {
    return null
  }
}

let loadPromise = null

// drop everything this module holds for the OUTGOING user. logout is a router
// push to /login (navbar.vue, fi_top_nav.vue) -- the SPA is never reloaded, so
// favState and loadPromise survive into the next session. Without this reset,
// the next user to sign in inherits the previous user's starred keys: the
// memoized loadPromise short-circuits their own GET, and the bar renders
// whichever of the stale keys resolve against the NEW user's nav. Gates are
// re-applied, so the result looks like a plausible short list rather than an
// obvious break -- an admin's stars rendered for a driver as Dashboard / Site
// Map / Support Tickets. Worse, the next toggle persists the stale snapshot
// onto the new account, so the wrong list becomes their real one.
//
// called from login.vue's created(), alongside the localStorage.clear() that
// covers the same "session is over" moment for everything else.
export function resetFavorites() {
  if (saveTimer) {
    clearTimeout(saveTimer)
    saveTimer = null
  }
  // a queued follow-up write would carry the outgoing user's snapshot. an
  // already-in-flight one cannot be recalled, but it is authenticated as -- and
  // targeted at -- that same outgoing user, so it lands where it belongs.
  pending = false
  favState.keys = []
  favState.loaded = false
  loadPromise = null
}

// fetches once per page load. safe to call from every consumer's mounted().
export function ensureFavoritesLoaded() {
  if (loadPromise) return loadPromise
  const cached = readCachedUserFavorites()
  if (cached) favState.keys = cached

  loadPromise = (async () => {
    // no session yet (login screen): nothing to fetch. noAuthRedirect: this
    // is a background convenience fetch, so a 403 rejects here instead of
    // bouncing the reader to login (networking/index.js, FI-2180)
    if (!localStorage.getItem('jwt')) return
    try {
      const { value: { data = {} } = {} } = await get_nav_favorites({ noAuthRedirect: true })
      // the server is the source of truth, including when it says "none":
      // an empty list is a real answer (the user cleared them elsewhere) and
      // must overwrite the optimistic paint above, not be mistaken for it
      if (Array.isArray(data.nav_favorites)) {
        favState.keys = data.nav_favorites
      } else {
        // key absent = first run for this user. adopt whatever the old local
        // report stars held so the migration is invisible, then write it back
        const legacy = readLegacyFavorites()
        favState.keys = legacy
        if (legacy.length) persist()
      }
    } catch (_) {
      // favorites are a convenience; a failed fetch must never block the nav
    } finally {
      favState.loaded = true
    }
  })()
  return loadPromise
}

// coalesce bursts (starring several pages quickly) into one write
let saveTimer = null
let writing = false
let pending = false

function persist() {
  if (saveTimer) clearTimeout(saveTimer)
  saveTimer = setTimeout(flush, 400)
}

// at most ONE write in flight, always carrying the latest state. a second
// toggle arriving mid-write sets `pending` and is picked up when the first
// settles, rather than queueing behind it: the payload is a full replacement,
// so overlapping writes could otherwise land out of order and a stalled one
// would wedge every future save.
async function flush() {
  saveTimer = null
  if (writing) { pending = true; return }
  if (!localStorage.getItem('jwt')) return

  writing = true
  const snapshot = favState.keys.slice()
  try {
    await update_nav_favorites(snapshot)
    // keep the cached login payload in step so a reload paints correctly
    // before the GET returns
    const user = JSON.parse(localStorage.getItem('user') || 'null')
    if (user) {
      user.preferences = user.preferences || {}
      user.preferences.nav_favorites = snapshot
      localStorage.setItem('user', JSON.stringify(user))
    }
  } catch (_) {
    // leave the optimistic local state in place: the next toggle retries,
    // and a hard failure resolves itself on the next load's GET
  } finally {
    writing = false
    // fire-and-forget: this is the tail of a settled write, not a new burst
    if (pending) { pending = false; flush() }
  }
}
